import { Injectable } from '@angular/core';
import { ForceOptionComponentEnum, PoComboOption, PoDynamicFormField, PoSelectOption, PoTableColumn, PoTableColumnLabel } from '@po-ui/ng-components';
import { LiteralsWords } from './../../../i18n/literals.model';
import { LiteralsService } from '../literals-service';
import { NetworkService } from '../network.service';
import { FilterPDFCustom, IPdfCustomAPI, IpdfCustomConfiguration, IPdfCustomFields, IPdfCustomHeader, IPdfCustomSection, IPdfCustomSectionAPI, IPdfDefaultLogoAPI } from '../../interfaces/pdf-custom.model';
import { TypeOfPdf, TypeOfPdfSection } from '../../enum/pdf-custom.enum';
import { AbstractControl, AsyncValidatorFn, UntypedFormBuilder, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { SchemaFieldsService } from '../schema-fields.service';
import { SchemaEnum } from '../../interfaces/schema-fields.model';
import { firstValueFrom, from, map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IQuickFilter } from '../../interfaces/IQuickFilter';
import { CustomFieldsService } from '../custom-fields.service';

@Injectable({
  providedIn: 'root'
})

export class PdfCustomService {
  literals: LiteralsWords;

  constructor(
    private literalsService: LiteralsService,
    protected formBuilder: UntypedFormBuilder,
    protected formItem: UntypedFormBuilder,
    protected networkService: NetworkService,
    protected schemaFieldsService: SchemaFieldsService,
    protected http: HttpClient,
    protected customFields: CustomFieldsService
  ) {
    this.literals = this.literalsService.getLiteralModule();
  }

  async getColumnsTable(): Promise<PoTableColumn[]> {
    return [
      {
        property: 'type',
        type: 'label',
        label: `${this.literals.str0096}`,
        labels: this.getLabelsOfPdfCustom()
      },
      {
        property: 'id',
        label: `${this.literals.str0110}`,
        type: 'string',
        visible: true
      },
      {
        property: 'title',
        type: 'string',
        label: `${this.literals.str0120}`,
        visible: true
      },
      {
        property: 'description',
        type: 'string',
        label: `${this.literals.str0047}`,
        visible: true
      },
      {
        property: 'url_image',
        type: 'string',
        label: `${this.literals.str0863}`,
        visible: true
      }
    ];
  }

  getLabelsOfPdfCustom(): PoTableColumnLabel[] {
    return [
      {
        value: TypeOfPdf.ORDER,
        color: 'color-10',
        label: this.literals.str0015,
        tooltip: this.literals.str0015
      },
      {
        value: TypeOfPdf.BUDGET,
        color: 'color-11',
        label: this.literals.str0009,
        tooltip: this.literals.str0009
      }
    ];
  }

  getNew(typeOfPdf: TypeOfPdf): IPdfCustomHeader {
    return {
      title: '',
      description: '',
      type: typeOfPdf,
      url_image: '',
      image: ''
    };
  }

  getNewCode(): string {
    const code = 1;
    return code.toString().padStart(6, '0'); // Garante que o código tenha 6 dígitos
  }

  getNewSection(typeOfPdfCustom: TypeOfPdfSection, order: number): IPdfCustomSection {
    return {
      order: order,
      type: typeOfPdfCustom,
      title: '',
      description: '',
      isDeleted: false,
      fields: [],
      deletedFields: []
    } as IPdfCustomSection;
  }

  getHeaderForm(pdfCustomHeader: IPdfCustomHeader): UntypedFormGroup {
    return this.formBuilder.group({
      id: [pdfCustomHeader.id],
      title: [pdfCustomHeader.title],
      description: [pdfCustomHeader.description],
      type: [pdfCustomHeader.type],
      url_image: [pdfCustomHeader.url_image, [], this.validateImageUrl()],
      image: [pdfCustomHeader.image]
    });
  }

  getSectionForm(pdfCustomSection: IPdfCustomSection): UntypedFormGroup {
    return this.formBuilder.group({
      id: [pdfCustomSection?.id],
      order: [pdfCustomSection.order],
      title: [pdfCustomSection.title],
      description: [pdfCustomSection.description],
      type: [pdfCustomSection.type],
      isDeleted: [false]
    });
  }

  getFieldsSectionForDynamicForm(): PoDynamicFormField[] {
    return [
      {
        property: 'title',
        label: this.literals.str0876,
        gridColumns: 6,
        disabled: true
      },
      {
        property: 'description',
        label: this.literals.str0877,
        gridColumns: 12,
        disabled: true
      },
      {
        property: 'type',
        label: this.literals.str0096,
        optionsService: '',
        gridColumns: 4,
        disabled: true,
        options: this.getOptionsSectionType(),
        forceOptionsComponentType: ForceOptionComponentEnum.select,
        fieldLabel: 'label',
        fieldValue: 'value'
      }
    ];
  }

  getSchemaForProperty(typePDF: TypeOfPdf, typeSection: TypeOfPdfSection): SchemaEnum {

    if (typePDF === TypeOfPdf.ORDER) {
      if (typeSection === TypeOfPdfSection.HEADER) {
        return SchemaEnum.salesOrders;
      } else {
        return SchemaEnum.salesOrdersItems;
      }
    } else if (typeSection === TypeOfPdfSection.HEADER) {
      return SchemaEnum.salesBudgets;
    } else {
      return SchemaEnum.salesBudgetsItems;
    }
  }

  async getFieldsSectionForTable(typePDF: TypeOfPdf, typeSection: TypeOfPdfSection): Promise<PoTableColumn[]> {
    const schema = this.getSchemaForProperty(typePDF, typeSection);
    const labelsProperty = await this.getOptionsFieldProperty<PoTableColumnLabel>(schema);
    return [
      { property: 'property', label: this.literals.str0881, type: 'label', labels: labelsProperty },
      { property: 'title', label: this.literals.str0120, type: 'string' },
      { property: 'type', label: this.literals.str0096, type: 'label', labels: this.getOptionsFieldType<PoTableColumnLabel>() },
      { property: 'format', label: this.literals.str0882, type: 'label', labels: this.getOptionsFieldFormat<PoTableColumnLabel>() },
      { property: 'decimal', label: this.literals.str0883, type: 'number' },
      { property: 'thousandsSeparator', label: this.literals.str0884, type: 'boolean' }
    ];
  }

  async getFieldsSectionForForm(typePDF: TypeOfPdf, typeSection: TypeOfPdfSection): Promise<PoDynamicFormField[]> {
    const schema = this.getSchemaForProperty(typePDF, typeSection);
    const unusedFields = ['internalid', 'externalid', 'customerinternalid', 'paymentinternalid', 'customerinternalid', 'customerid', 'sellerid', 'typeofoperation', 'deleted', 'STAMP', 'intermediaryid', 'orderinternalid', 'externalitemid','prospectstore','customerstore','store'];
    const optionsProperty = await this.getOptionsFieldProperty<PoSelectOption>(schema);
    optionsProperty.forEach(option => {
      switch (option.value) {
        case 'sellername':
          option.label = this.literals.str0215;
          break;
        case 'customername':
          option.label = this.literals.str0011;
          break;
        case 'carriername':
          option.label = this.literals.str0228;
          break;
        default:
          break;
      }
    });

    const filteredOptions = optionsProperty.filter((option, index, self) => {
      return index === self.findIndex(t => (
        t.value === option.value
      ));
    }).filter(option => {
      return !unusedFields.includes(String(option.value));
    });

    return [
      {
        property: 'property',
        label: this.literals.str0881,
        gridColumns: 4,
        optionsService: '',
        options: filteredOptions,
        fieldLabel: 'label',
        fieldValue: 'value',
        required: true,
        showRequired: true
      },
      {
        property: 'title',
        label: this.literals.str0120,
        gridColumns: 6,
        required: true,
        showRequired: true
      },
      {
        property: 'type',
        label: this.literals.str0096,
        optionsService: '',
        options: this.getOptionsFieldType<PoSelectOption>(),
        fieldLabel: 'label',
        fieldValue: 'value',
        disabled: true,
        showRequired: true
      },
      {
        property: 'format',
        label: this.literals.str0882,
        gridColumns: 3,
        optional: false,
        options: this.getOptionsFieldFormat<PoSelectOption>(),
        forceOptionsComponentType: ForceOptionComponentEnum.select,
        fieldLabel: 'label',
        fieldValue: 'value'
      },
      {
        property: 'decimal',
        label: this.literals.str0883,
        gridColumns: 3,
        type: 'number'
      },
      {
        property: 'thousandsSeparator',
        label: this.literals.str0884,
        type: 'boolean',
        booleanTrue: this.literals.str0545,
        booleanFalse: this.literals.str0241
      }
    ];
  }

  getOptionsCustomFields(type: string): string {
    if (type === 'String') {
      type = '1'
    }
    if (type === 'Number' || type === 'Currency') {
      type = '2'
    }
    if (type === 'Date') {
      type = '3'
    }
    return type
  }

  getOptionsFieldType<T>(): T[] {
    return [
      {
        value: 'C',
        label: this.literals.str0889
      },
      {
        value: 'N',
        label: this.literals.str0890
      },
      {
        value: 'D',
        label: this.literals.str0035
      },
      {
        value: 'L',
        label: this.literals.str0891
      },
      {
        value: '',
        label: 'Não definido'
      }
    ] as T[];
  }

  getOptionsFieldFormat<T>(): T[] {
    return [
      {
        value: '1',
        label: this.literals.str0078
      },
      {
        value: '2',
        label: this.literals.str0097
      },
      {
        value: '',
        label: 'Não definido'
      }
    ] as T[];
  }

  async getOptionsFieldProperty<T>(schema: SchemaEnum): Promise<T[]> {
    const properties: T[] = [];
    const fields = await this.schemaFieldsService.getById(schema);
    const totalCustomFields = await this.customFields.getCustomFieldOnStorage(schema);
    fields['items'].forEach(field => {
      const itemOption = {
        value: field['field'],
        label: field['label']
      } as T;
      properties.push(itemOption);
    });
    if (totalCustomFields) {
      const customFields = JSON.parse(totalCustomFields);
      customFields.forEach(customField => {
        const customItemOption = {
          value: customField['property'],
          label: customField['label']
        } as T;
        properties.push(customItemOption);
      });
    }
    return properties;
  }

  async getPropertiesData(schema: SchemaEnum): Promise<Array<{ property: string, label: string, type: string }>> {
    const propertiesData = new Array<{ property: string, label: string, type: string }>();
    const listOfFieldTypes = this.getOptionsFieldType<{ label: string, value: string }>();
    const dataPropertyList = await this.schemaFieldsService.getById(schema);
    dataPropertyList['items'].forEach(property => {
      const propertyData: { property: string, label: string, type: string } = {
        property: property['field'],
        label: property['label'],
        type: listOfFieldTypes[property['type'] - 1]['value']
      }
      propertiesData.push(propertyData);
    });
    const totalCustomFields = await this.customFields.getCustomFieldOnStorage(schema);
    if (totalCustomFields) {
      const customFields = JSON.parse(totalCustomFields);
      customFields.forEach(customField => {
        customField['type'] = this.getOptionsCustomFields(customField['type']);
        const customPropertyData: { property: string, label: string, type: string } = {
          property: customField['property'],
          label: customField['label'],
          type: listOfFieldTypes[customField['type'] - 1]['value']
        }
        propertiesData.push(customPropertyData);
      });
    }
    return propertiesData;
  }

  getNewField(order: number): IPdfCustomFields {
    return {
      order: order,
      title: '',
      property: '',
      type: '',
      format: '',
      decimal: 0,
      thousandseparator: false,
      isDeleted: false
    }
  }

  getTypeOfPdfOptions(typeOfPdf: string): string {
    if (typeOfPdf === TypeOfPdf.ORDER) {
      return this.literals.str0017;
    } else {
      return this.literals.str0022;
    }
  }

  getPdfOptions(): PoComboOption[] {
    return [
      {
        value: '1',
        label: this.literals.str0017
      },
      {
        value: '2',
        label: this.literals.str0022
      }
    ];
  }

  getOptionsSectionType(): PoComboOption[] {
    return [
      {
        value: '1',
        label: this.literals.str0880
      },
      {
        value: '2',
        label: this.literals.str0243
      }
    ];
  }

  async save(pdfCustom: IpdfCustomConfiguration): Promise<IpdfCustomConfiguration> {
    return new Promise<IpdfCustomConfiguration>((resolve, reject) => {
      const sucesso = true; // Simulando uma condição
      if (sucesso) {
        resolve(pdfCustom);
      } else {
        reject("Ocorreu um erro.");
      }
    });
  }

  async getDataForPdfCustom(page = 1): Promise<IPdfCustomAPI> {
    return firstValueFrom(this.getHeaderPdfCustom(undefined, page));
  }

  getHeaderPdfCustom(pdfCustomId = '', page = 1, pageSize = 10, filter = '', fields = '', sort = ''): Observable<IPdfCustomAPI> {
    const parameters = new HttpParams()
      .append('page', page.toString())
      .append('pageSize', pageSize.toString())
      .append('FILTER', filter)
      .append('FIELDS', fields)
      .append('SORT', sort);

    const url = `${environment.url}api/pgv/pdfcustom/${pdfCustomId}`;
    return this.http.get<IPdfCustomAPI>(url, { params: parameters });
  }

  getSectionsPdfCustom(pdfCustomId: string): Observable<IPdfCustomSectionAPI> {
    const url = `${environment.url}api/pgv/pdfcustom/${pdfCustomId}/sections`;
    return this.http.get<IPdfCustomSectionAPI>(url);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getFieldsPdfCustom(pdfCustomId: string, pdfCustomSectionId: string): Observable<any> {
    const url = `${environment.url}api/pgv/pdfcustom/${pdfCustomId}/sections/${pdfCustomSectionId}/fields`;
    return this.http.get(url);
  }

  isOnline(): boolean {
    return this.networkService.getConnectionStatus().status;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createPdfCustom(body: IpdfCustomConfiguration): Observable<any> {
    return this.http.post(`${environment.url}api/pgv/pdfcustom`, body);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatePdfCustom(body: IpdfCustomConfiguration): Observable<any> {
    return this.http.put(`${environment.url}api/pgv/pdfcustom/${body.id}`, body);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deletePdfCustom(pdfCustom: IPdfCustomHeader): Observable<any> {
    return this.http.delete(
      `${environment.url}api/pgv/pdfcustom/${pdfCustom.id}`
    );
  }

  buildFastFilter(search: string): string | IQuickFilter {
    return {
      inputSearch: search,
      fields: ['id', 'type', 'description']
    };
  }

  checkFilterTextIsValid(searchInputValue: string): boolean {
    return searchInputValue !== undefined &&
      searchInputValue != null;
  }

  checkFilterTextEnoughToFilter(searchInputValue: string): boolean {
    return searchInputValue.length >= 3;
  }

  checkFilterTextEmpty(searchInputValue: string): boolean {
    return searchInputValue.length === 0;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getFastFilter(page: number, pageSize: number, filter: any, sort: string, fields: string): Promise<IPdfCustomAPI> {
    const newFilter = this.buildFilter(filter);
    const data$ = this.getHeaderPdfCustom(undefined, page, pageSize, newFilter, fields, sort);
    return firstValueFrom(data$);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buildFilter(filter: any): string {
    if (filter.inputSearch !== undefined) {
      return this.buildFilterContainsOr(filter);
    } else {
      return this.buildFilterAnd(filter);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buildFilterContainsOr(filter: any): string {
    let queryParam = '';

    if (filter.fields) {
      filter.fields.map((atr, index) => {
        if (index === 0) {
          queryParam += filter.additionalFilter ? '(' : '';
          queryParam += `contains(${atr}, '${filter.inputSearch}')`;
        } else {
          queryParam += ` or contains(${atr}, '${filter.inputSearch}')`;
        }
      });
    }

    if (filter.additionalFilter) {
      queryParam += queryParam
        ? `) and ${filter.additionalFilter}`
        : filter.additionalFilter;
    }

    return queryParam;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buildFilterAnd(filter: any): string {
    return this.buildFilterEqual(filter).join('and ');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buildFilterEqual(filter): Array<any> {
    return Object.keys(filter).map(atr => `${atr} eq '${filter[atr]}'`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buildAdvancedFilterForDisclaimer(filters: Array<any>): any {
    const filterModel = {
      id: null,
      description: null,
      type: null
    };
    return this.returnFilter(filters, filterModel);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  returnFilter(filters: Array<any>, filterModel: any): any {
    const filter = this.buildTransformFilter(filters);
    filter.forEach(response => {
      Object.keys(filterModel).forEach(item => {
        if (response[0] === item) {
          filterModel[item] = response[1];
        }
      });
    });
    this.deleteNullOfFilter(filterModel);
    return filterModel;
  }

  deleteNullOfFilter(expression = {}): object {
    Object.keys(expression).forEach(item => {
      if (expression[item] === null) {
        delete expression[item];
      }
    });

    return expression;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buildTransformFilter(filters: Array<any>): Array<any> {
    let property: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any;

    let transformFilter = [];

    filters.forEach(item => {
      property = item.property;
      value = item.value;
      transformFilter = this.addFilteredItems(property, value, transformFilter);
    });

    return transformFilter;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addFilteredItems(property: string, value: any, transformFilter: any): Array<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filter: Array<any> = [property, value];
    transformFilter = [...transformFilter, filter];
    return transformFilter;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAdvancedFilter(page: number, pageSize: number, filter: any, fields: string): Promise<any> {
    const newFilter = this.buildAdvancedFilterContains(filter);
    const data$ = this.getHeaderPdfCustom(undefined, page, pageSize, newFilter, fields);
    return firstValueFrom(data$);
  }

  getDataForAdvancedAndFastFilter(
    page: number,
    pageSize: number,
    fastFilter: string | IQuickFilter,
    advancedFilter: FilterPDFCustom,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filterAdvancedItems: Array<any>,
    sort?: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fields?: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    const unionFilter = this.buildAdvancedAndFastFilter(fastFilter, advancedFilter);
    const data$ = this.getHeaderPdfCustom(undefined, page, pageSize, unionFilter, fields, sort);
    return firstValueFrom(data$);
  }

  buildAdvancedAndFastFilter(fastFilter: string | IQuickFilter, advancedFilter: FilterPDFCustom): string {
    const buildFastFilter = this.buildFilter(fastFilter);
    const buildAdvancedFilter = this.buildAdvancedFilterContains(advancedFilter);

    if (buildFastFilter !== '' && buildAdvancedFilter !== '') {
      if (buildAdvancedFilter.includes('eq')) {
        return `(${buildFastFilter}) and ${buildAdvancedFilter}`;
      } else {
        return `(${buildAdvancedFilter}) and ${buildFastFilter}`;
      }
    } else if (buildFastFilter === '' && buildAdvancedFilter !== '') {
      return buildAdvancedFilter;
    } else if (buildFastFilter !== '' && buildAdvancedFilter === '') {
      return buildFastFilter;
    } else {
      return '';
    }
  }

  buildAdvancedFilterContains(filter: FilterPDFCustom): string {
    let queryParam = '';

    Object.keys(filter).forEach((property, index) => {
      if (index === 0) {
        queryParam += `contains(${property}, '${filter[property]}')`;
      } else {
        queryParam += ` and contains(${property}, '${filter[property]}')`;
      }
    });
    return queryParam;
  }

  validateImageUrl(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return from(this.imageUrlIsValid(control.value)).pipe(
        map((isValid: boolean) => (isValid ? null : { invalidAsync: true }))
      );
    };
  }

  async imageUrlIsValid(url: string): Promise<boolean> {
    try {
      const response = await fetch(url);
      return response.ok;
    } catch {
      return false;
    }
  }

  async updateDefaultLogo(url_image: string): Promise<IPdfDefaultLogoAPI> {
    return await firstValueFrom(this.http.put(`${environment.url}api/pgv/pdfcustom/defaultlogo`, { 'url-image': url_image.trim() }));
  }
}
